import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  Dropdown,
  DropdownButton,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import {
  FaClock,
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaFilter,
  FaEye,
  FaExchangeAlt,
  FaStore,
  FaInfoCircle,
} from "react-icons/fa";
import { format } from "date-fns";

const UnassignedBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignBooking, setReassignBooking] = useState(null);
  const [showVendorDetailsModal, setShowVendorDetailsModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Fetch unassigned bookings
  const fetchUnassignedBookings = async () => {
    try {
      setLoading(true);
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/unassigned?limit=${pagination.limit}`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch unassigned bookings");
      }

      const data = await response.json();
      let filteredBookings = data.bookings || [];

      // Apply filters
      if (filters.category) {
        filteredBookings = filteredBookings.filter(
          (b) =>
            b.category?.toLowerCase() === filters.category.toLowerCase() ||
            b.serviceName?.toLowerCase().includes(filters.category.toLowerCase())
        );
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredBookings = filteredBookings.filter(
          (b) =>
            b.customerName?.toLowerCase().includes(searchLower) ||
            b.serviceName?.toLowerCase().includes(searchLower) ||
            b.address?.toLowerCase().includes(searchLower) ||
            b.customerPhone?.includes(searchLower)
        );
      }

      // Sort bookings by createdAt in descending order (latest first)
      filteredBookings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setBookings(filteredBookings);
      setPagination((prev) => ({ ...prev, total: filteredBookings.length }));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching unassigned bookings:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch available vendors for assignment
  const fetchAvailableVendors = async (category, pincode) => {
    try {
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      let url = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/admin/available-vendors`;
      const params = new URLSearchParams();
      if (category) params.append("category", category);
      if (pincode) params.append("pincode", pincode);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch available vendors");
      }

      const data = await response.json();
      setAvailableVendors(data.vendors || []);
    } catch (err) {
      console.error("Error fetching available vendors:", err);
      setAvailableVendors([]);
    }
  };

  // Assign vendor to booking
  const handleAssignVendor = async () => {
    console.log("handleAssignVendor called");
    console.log("selectedBooking:", selectedBooking);
    console.log("selectedVendorId:", selectedVendorId);
    
    if (!selectedBooking || !selectedVendorId) {
      console.log("Missing required data:", { selectedBooking, selectedVendorId });
      alert("Please select a vendor");
      return;
    }

    try {
      setAssigning(true);
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      console.log("Calling assign API...");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${selectedBooking.id}/assign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ vendorId: selectedVendorId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign vendor");
      }

      const result = await response.json();
      console.log("Assignment result:", result);
      alert(`✅ ${result.message}`);

      // Refresh bookings list
      fetchUnassignedBookings();
      setShowAssignModal(false);
      setSelectedBooking(null);
      setSelectedVendorId("");
    } catch (err) {
      console.error("Error assigning vendor:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  // Open assign modal and fetch available vendors
  const openAssignModal = async (booking) => {
    setSelectedBooking(booking);
    setSelectedVendorId("");
    await fetchAvailableVendors(booking.category, booking.pincode);
    setShowAssignModal(true);
  };

  // View booking details
  const viewBookingDetails = (booking) => {
    setDetailsBooking(booking);
    setShowDetailsModal(true);
  };

  // View vendor details - fetch complete vendor data from API
  const viewVendorDetails = async (vendor) => {
    try {
      console.log("Fetching complete vendor details for:", vendor.id);
      
      const adminToken = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
      
      // Fetch complete vendor details from your API
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/vendors/${vendor.id}`,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch vendor details");
      }
      
      const data = await response.json();
      const completeVendor = data.vendor || data;
      
      console.log("Complete vendor data:", completeVendor);
      console.log("Vendor ID from API:", vendor.id, "or", completeVendor.id);
      
      // Extract data from the complete vendor object
      const personalInfo = completeVendor.vendor?.personal_info || {};
      const businessInfo = completeVendor.vendor || {};
      const categories = businessInfo.categories || completeVendor.categories || [];
      
      // Flatten services from all categories
      const allServices = [];
      categories.forEach(category => {
        if (category.services && Array.isArray(category.services)) {
          category.services.forEach(service => {
            allServices.push({
              category: category.category_name,
              category_name: category.category_name,
              service_name: service.service_name,
              type: service.service_name,
              price: service.price,
              description: service.description,
              packageId: service.packageId,
              subCategoryId: service.subCategoryId
            });
          });
        }
      });
      
      // IMPORTANT: Preserve the vendor ID from the original vendor object
      const vendorId = vendor.id || completeVendor.id;
      
      // Create enriched vendor object with all needed fields
      const enrichedVendor = {
        ...completeVendor,
        id: vendorId, // Explicitly set the ID
        name: personalInfo.name || completeVendor.name || vendor.name || "N/A",
        email: personalInfo.email || completeVendor.email || "N/A",
        phone: personalInfo.phone || completeVendor.phone || vendor.phone || "N/A",
        address: personalInfo.address || completeVendor.address || "N/A",
        pincode: personalInfo.pincode || completeVendor.pincode || vendor.pincode || "N/A",
        societyName: personalInfo.societyName || completeVendor.societyName || "N/A",
        about: personalInfo.about || completeVendor.about || completeVendor.businessDescription || "No description provided",
        profileImage: personalInfo.profile_image_url || completeVendor.profileImage || vendor.profileImage || null,
        businessCategory: completeVendor.businessCategory || categories[0]?.category_name || "General",
        isAvailable: completeVendor.isAvailable === true,
        rating: completeVendor.reviews?.averageRating || completeVendor.averageRating || 0,
        totalReviews: completeVendor.reviews?.totalReviews || completeVendor.totalReviews || 0,
        activeBookings: completeVendor.activeBookings || vendor.activeBookings || 0,
        services: allServices,
        documents: personalInfo.documents || {},
        verificationStatus: completeVendor.verificationStatus || "not_submitted",
        subscription: completeVendor.subscription || null,
        securityDeposit: completeVendor.securityDeposit || null,
        availability: businessInfo.availability || completeVendor.availability || [],
        workingAreas: businessInfo.working_areas || completeVendor.working_areas || [],
        rawVendorData: completeVendor
      };
      
      console.log("Enriched vendor ID:", enrichedVendor.id);
      console.log("Enriched vendor with email:", enrichedVendor.email);
      console.log("Enriched vendor with address:", enrichedVendor.address);
      console.log("Enriched vendor with society:", enrichedVendor.societyName);
      
      setSelectedVendor(enrichedVendor);
      setShowVendorDetailsModal(true);
    } catch (err) {
      console.error("Error fetching vendor details:", err);
      alert("Failed to load vendor details. Please try again.");
    }
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      unassigned: { variant: "warning", text: "Awaiting Assignment" },
      assigned: { variant: "info", text: "Assigned" },
      accepted: { variant: "success", text: "Accepted" },
      rejected: { variant: "danger", text: "Rejected" },
      in_progress: { variant: "primary", text: "In Progress" },
      completed: { variant: "secondary", text: "Completed" },
      cancelled: { variant: "dark", text: "Cancelled" },
    };

    const config = statusConfig[status] || { variant: "secondary", text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy • h:mm a");
    } catch {
      return dateString;
    }
  };

  // Calculate wait time
  const getWaitTime = (createdAt) => {
    try {
      const created = new Date(createdAt);
      const now = new Date();
      const diffMinutes = Math.floor((now - created) / (1000 * 60));
      
      if (diffMinutes < 60) {
        return `${diffMinutes} min${diffMinutes !== 1 ? "s" : ""}`;
      }
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    } catch {
      return "Unknown";
    }
  };

  useEffect(() => {
    fetchUnassignedBookings();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnassignedBookings, 30000);
    return () => clearInterval(interval);
  }, [pagination.limit]);

  // Refresh when filters change
  useEffect(() => {
    fetchUnassignedBookings();
  }, [filters.category]);

  if (loading && bookings.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading unassigned bookings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error Loading Bookings</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchUnassignedBookings}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <div>
          <h2 className="mb-1 fw-bold">
            <FaClock className="text-warning me-2" />
            Unassigned Bookings Queue
          </h2>
          <p className="text-muted small mb-0">
            {bookings.length} booking{bookings.length !== 1 ? "s" : ""} waiting for vendor assignment
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchUnassignedBookings}>
          <FaSearch className="me-1" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by customer name, service, address, phone..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />
                {filters.search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setFilters({ ...filters, search: "" })}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Filter by category/service"
                  value={filters.category}
                  onChange={(e) =>
                    setFilters({ ...filters, category: e.target.value })
                  }
                />
                {filters.category && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setFilters({ ...filters, category: "" })}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} md={2}>
              <Form.Select
                value={pagination.limit}
                onChange={(e) =>
                  setPagination({ ...pagination, limit: parseInt(e.target.value), page: 1 })
                }
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Bookings Table */}
      {bookings.length === 0 ? (
        <Alert variant="info" className="text-center">
          <h5>No unassigned bookings</h5>
          <p className="mb-0">All bookings have been assigned to vendors.</p>
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table striped hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Location</th>
                <th>Price</th>
                <th>Created</th>
                <th>Wait Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.slice(
                (pagination.page - 1) * pagination.limit,
                pagination.page * pagination.limit
              ).map((booking) => (
                <tr key={booking.id}>
                  <td>
                    <code className="small">{booking.id}</code>
                  </td>
                  <td>
                    <div className="fw-semibold">{booking.customerName}</div>
                    <small className="text-muted">
                      <FaPhone className="me-1" size={12} />
                      {booking.customerPhone}
                    </small>
                  </td>
                  <td>
                    <div>{booking.serviceName}</div>
                    <small className="text-muted">
                      {booking.category || "No category"}
                    </small>
                  </td>
                  <td>
                    <div className="small">{booking.address?.substring(0, 50)}...</div>
                    <small className="text-muted">
                      {booking.pincode || "No pincode"}
                    </small>
                  </td>
                  <td>
                    <span className="fw-semibold">₹{booking.price}</span>
                    <br />
                    <Badge bg="light" text="dark" className="small">
                      {booking.paymentMode?.toUpperCase() || "COD"}
                    </Badge>
                  </td>
                  <td>
                    <div className="small">{formatDate(booking.createdAt)}</div>
                  </td>
                  <td>
                    <Badge 
                      bg={getWaitTime(booking.createdAt).includes("hour") ? "danger" : "warning"}
                      className="px-3 py-2"
                    >
                      {getWaitTime(booking.createdAt)}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => viewBookingDetails(booking)}
                        title="View Details"
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => openAssignModal(booking)}
                        title="Assign Vendor"
                      >
                        <FaExchangeAlt className="me-1" />
                        Assign
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {bookings.length > pagination.limit && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.Prev
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
              }
              disabled={pagination.page === 1}
            />
            {[...Array(Math.ceil(bookings.length / pagination.limit))].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={pagination.page === idx + 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: idx + 1 }))}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              disabled={
                pagination.page === Math.ceil(bookings.length / pagination.limit)
              }
            />
          </Pagination>
        </div>
      )}

      {/* Assign Vendor Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExchangeAlt className="me-2" />
            Assign Vendor to Booking
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <h6>Booking Details:</h6>
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Service:</strong> {selectedBooking.serviceName}
                    </div>
                    <div className="mb-2">
                      <strong>Customer:</strong> {selectedBooking.customerName}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong> {selectedBooking.customerPhone}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Address:</strong> {selectedBooking.address}
                    </div>
                    <div className="mb-2">
                      <strong>Price:</strong> ₹{selectedBooking.price}
                    </div>
                    <div className="mb-2">
                      <strong>Created:</strong> {formatDate(selectedBooking.createdAt)}
                    </div>
                  </Col>
                </Row>
              </div>

              <h6>Available Vendors:</h6>
              {availableVendors.length === 0 ? (
                <Alert variant="warning">
                  No available vendors found for this category/pincode.
                  <hr />
                  <small>
                    Ensure vendors have:
                    <ul className="mt-2 mb-0">
                      <li>Active subscription</li>
                      <li>Security deposit paid</li>
                      <li>Available status enabled</li>
                      <li>Matching service category</li>
                    </ul>
                  </small>
                </Alert>
              ) : (
                <div className="vendors-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {availableVendors.map((vendor) => (
                    <Card
                      key={vendor.id}
                      className={`mb-2 cursor-pointer ${
                        selectedVendorId === vendor.id ? "border-primary bg-light" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <Card.Body className="py-2">
                        <Row className="align-items-center">
                          <Col md={5}>
                            <div className="fw-semibold">
                              <FaStore className="me-2 text-primary" />
                              {vendor.name}
                            </div>
                            <small className="text-muted">
                              <FaPhone className="me-1" size={12} />
                              {vendor.phone || "No phone"}
                            </small>
                          </Col>
                          <Col md={3}>
                            <div className="text-warning">
                              {"★".repeat(Math.floor(vendor.rating))}
                              {"☆".repeat(5 - Math.floor(vendor.rating))}
                              <span className="ms-1 text-muted small">
                                ({vendor.totalReviews})
                              </span>
                            </div>
                          </Col>
                          <Col md={2}>
                            <Badge
                              bg={vendor.canAcceptMore ? "success" : "danger"}
                              className="w-100 text-center"
                            >
                              {vendor.activeBookings || 0} / 5 Active
                            </Badge>
                          </Col>
                          <Col md={2} className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewVendorDetails(vendor);
                                }}
                                title="View Vendor Details"
                              >
                                <FaInfoCircle className="me-1" />
                                See Details
                              </Button>
                              {selectedVendorId === vendor.id && (
                                <FaCheckCircle className="text-success" size={20} />
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleAssignVendor}
            disabled={!selectedVendorId || assigning || availableVendors.length === 0}
          >
            {assigning ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              <>
                <FaCheckCircle className="me-2" />
                Assign Vendor
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Booking Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailsBooking && (
            <>
              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">Booking Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Booking ID:</strong> <code>{detailsBooking.id}</code>
                      </div>
                      <div className="mb-2">
                        <strong>Status:</strong> {getStatusBadge(detailsBooking.status)}
                      </div>
                      <div className="mb-2">
                        <strong>Created:</strong> {formatDate(detailsBooking.createdAt)}
                      </div>
                      <div className="mb-2">
                        <strong>Scheduled:</strong> {formatDate(detailsBooking.dateTime)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">Payment Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Amount:</strong> ₹{detailsBooking.price}
                      </div>
                      <div className="mb-2">
                        <strong>Payment Mode:</strong> {detailsBooking.paymentMode?.toUpperCase()}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">Customer Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Name:</strong> {detailsBooking.customerName}
                      </div>
                      <div className="mb-2">
                        <strong>Phone:</strong> {detailsBooking.customerPhone}
                      </div>
                      <div className="mb-2">
                        <strong>Address:</strong> {detailsBooking.address}
                      </div>
                      {detailsBooking.customerImage && (
                        <div className="mt-2">
                          <img
                            src={detailsBooking.customerImage}
                            alt="Customer"
                            className="rounded-circle"
                            width="50"
                            height="50"
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header className="bg-light">Service Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Service:</strong> {detailsBooking.serviceName}
                      </div>
                      <div className="mb-2">
                        <strong>Description:</strong> {detailsBooking.description}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {detailsBooking && detailsBooking.status === "unassigned" && (
            <Button
              variant="success"
              onClick={() => {
                setShowDetailsModal(false);
                openAssignModal(detailsBooking);
              }}
            >
              <FaExchangeAlt className="me-2" />
              Assign Vendor
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Vendor Details Modal */}
      <Modal show={showVendorDetailsModal} onHide={() => setShowVendorDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaStore className="me-2 text-primary" />
            Vendor Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor && (
            <>
              <Row>
                <Col md={12}>
                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-3">
                        {selectedVendor.profileImage ? (
                          <img
                            src={selectedVendor.profileImage}
                            alt={selectedVendor.name}
                            className="rounded-circle me-3"
                            width="80"
                            height="80"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                            style={{ width: "80px", height: "80px", fontSize: "32px" }}
                          >
                            {selectedVendor.name?.charAt(0) || "V"}
                          </div>
                        )}
                        <div>
                          <h4 className="mb-1">{selectedVendor.name}</h4>
                          <p className="text-muted mb-0">
                            <Badge bg="secondary" className="me-2">
                              {selectedVendor.businessCategory}
                            </Badge>
                            {selectedVendor.isAvailable ? (
                              <Badge bg="success">Available</Badge>
                            ) : (
                              <Badge bg="danger">Unavailable</Badge>
                            )}
                            {selectedVendor.verificationStatus === "approved" && (
                              <Badge bg="info" className="ms-2">Verified</Badge>
                            )}
                          </p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <strong>Contact Information</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Phone:</strong>{" "}
                        <a href={`tel:${selectedVendor.phone}`}>{selectedVendor.phone || "N/A"}</a>
                      </div>
                      <div className="mb-2">
                        <strong>Email:</strong> {selectedVendor.email || "N/A"}
                      </div>
                      <div className="mb-2">
                        <strong>Address:</strong> {selectedVendor.address || "N/A"}
                      </div>
                      <div className="mb-2">
                        <strong>Pincode:</strong> {selectedVendor.pincode || "N/A"}
                      </div>
                      {selectedVendor.societyName && (
                        <div className="mb-2">
                          <strong>Society:</strong> {selectedVendor.societyName}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <strong>Business Information</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>About:</strong> {selectedVendor.about}
                      </div>
                      <div className="mb-2">
                        <strong>Rating:</strong>{" "}
                        <span className="text-warning">
                          {"★".repeat(Math.floor(selectedVendor.rating))}
                          {"☆".repeat(5 - Math.floor(selectedVendor.rating))}
                        </span>{" "}
                        ({selectedVendor.totalReviews} reviews)
                      </div>
                      <div className="mb-2">
                        <strong>Active Bookings:</strong> {selectedVendor.activeBookings} / 5
                      </div>
                      {selectedVendor.subscription && selectedVendor.subscription.plan !== "none" && (
                        <div className="mb-2">
                          <strong>Subscription:</strong>{" "}
                          <Badge bg="success">
                            {selectedVendor.subscription.plan} - {selectedVendor.subscription.type}
                          </Badge>
                        </div>
                      )}
                      {selectedVendor.securityDeposit && selectedVendor.securityDeposit.paid && (
                        <div className="mb-2">
                          <strong>Security Deposit:</strong>{" "}
                          <Badge bg="success">Paid (₹{selectedVendor.securityDeposit.totalAmount})</Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Working Areas */}
              {selectedVendor.workingAreas && selectedVendor.workingAreas.length > 0 && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <strong>Working Areas</strong>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedVendor.workingAreas.map((area, idx) => (
                            <Badge key={idx} bg="light" text="dark" className="p-2">
                              {area}
                            </Badge>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Availability */}
              {selectedVendor.availability && selectedVendor.availability.length > 0 && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-3">
                      <Card.Header className="bg-light">
                        <strong>Working Days</strong>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedVendor.availability.map((day, idx) => (
                            <Badge key={idx} bg="info" className="p-2">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </Badge>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Services Offered */}
              {selectedVendor.services && selectedVendor.services.length > 0 && (
                <Row>
                  <Col md={12}>
                    <Card>
                      <Card.Header className="bg-light">
                        <strong>Services Offered ({selectedVendor.services.length})</strong>
                      </Card.Header>
                      <Card.Body>
                        <Table bordered size="sm" responsive>
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Service</th>
                              <th>Price</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedVendor.services.map((service, idx) => (
                              <tr key={idx}>
                                <td>{service.category}</td>
                                <td>{service.service_name}</td>
                                <td>₹{service.price}</td>
                                <td className="text-muted small">{service.description || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* If no services found, show message */}
              {(!selectedVendor.services || selectedVendor.services.length === 0) && (
                <Row>
                  <Col md={12}>
                    <Alert variant="info">
                      <small>No services have been added by this vendor yet.</small>
                    </Alert>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVendorDetailsModal(false)}>
            Close
          </Button>
          {/* Always show Select button if we have a vendor, regardless of selectedBooking */}
          {selectedVendor && (
            <Button
              variant="success"
              onClick={() => {
                console.log("Selecting vendor:", selectedVendor.id);
                console.log("Current selectedBooking:", selectedBooking);
                
                // Set the selected vendor ID
                setSelectedVendorId(selectedVendor.id);
                
                // Close the details modal
                setShowVendorDetailsModal(false);
                
                // Optional: Show a success message
                alert(`Vendor "${selectedVendor.name}" selected. Click "Assign Vendor" to confirm assignment.`);
              }}
            >
              <FaCheckCircle className="me-2" />
              Select This Vendor
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UnassignedBookings;